import React from 'react';

import {LibraryParameterDefinitionsContext} from 'procedures/parameter-library/LibraryParameterDefinitionsContext';
import {LibraryParameterDefinitionIdName} from 'procedures/parameter-library/ParameterLibraryTypes';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipSelectInput from 'ui-library/inputs/ChipSelectInput';
import {OptionData} from 'ui-library/menus/MenuConstants';
import {getMatchingOptionDatas} from 'ui-library/menus/MenuUtil';
import {InputChangeEvent} from 'util/Types';

export interface LibraryParameterDefinitionSearchInputProps {
  query: string;
  onQueryChange: (e: InputChangeEvent) => void;
  /** Invoked with id and name from the query when the user picks from the menu, or null when cleared. */
  onSelect: (definition: LibraryParameterDefinitionIdName | null) => void;
  selectedDefinition: LibraryParameterDefinitionIdName | null | undefined;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

/**
 * Typeahead input that searches library parameter definitions by name.
 * Definitions are provided via {@link LibraryParameterDefinitionsContext}.
 */
export default function LibraryParameterDefinitionSearchInput({
  query,
  onQueryChange,
  onSelect,
  selectedDefinition,
  onKeyDown,
}: LibraryParameterDefinitionSearchInputProps): JSX.Element {
  const definitions = LibraryParameterDefinitionsContext.useContext();

  const allOptionDatas: Array<OptionData> = definitions.map((d) => ({
    name: d.name,
    value: {id: d.id, name: d.name},
    pureTextName: d.name,
  }));
  const optionDatas = getMatchingOptionDatas(query, allOptionDatas);

  const value = selectedDefinition != null ? [selectedDefinition] : [];

  return (
    <ChipSelectInput
      placeholder=""
      optionDatas={value.length > 0 ? [] : optionDatas}
      query={query}
      value={value}
      onKeyDown={onKeyDown}
      onQueryChange={onQueryChange}
      scrollable={true}
      disableTyping={value.length > 0}
      getChipProps={(def: LibraryParameterDefinitionIdName) => ({
        text: def.name,
        size: ChipSize.TABLE_CELL,
        icon: 'parameters',
      })}
      onSelect={(selected: LibraryParameterDefinitionIdName | null): void => {
        onSelect(selected ?? null);
      }}
      onRemove={() => {
        onSelect(null);
      }}
    />
  );
}
